import { LoadingOutlined } from '@ant-design/icons'
import { AutoComplete, Form, Modal, Tag, message } from 'antd'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import {
  getAllTags,
  createClientTag,
  deleteClientTag,
} from '../../network/ClientTags'

const TagSelector = ({ field, form, ...restProps }) => {
  const [searchValue, setSearchValue] = useState('')
  const [selectedTags, setSelectedTags] = useState([])
  const [isCreating, setIsCreating] = useState(false)

  // Fetch existing tags
  const { data: tagsData, refetch } = useQuery(
    ['clientTags', { sort: ['id', 'ASC'], range: [0, 999], filter: {} }],
    getAllTags,
    {
      select: (data) => data?.list || [],
    }
  )

  // Initialize selectedTags when component mounts or when form field value changes
  useEffect(() => {
    const fieldValue = form.getFieldValue(field.key)
    if (
      fieldValue &&
      Array.isArray(fieldValue) &&
      tagsData &&
      tagsData.length > 0
    ) {
      const existingTags = tagsData.filter((tag) => fieldValue.includes(tag.id))
      setSelectedTags(existingTags)
    }
  }, [tagsData, form, field.key])

  // Create new tag mutation
  const createTagMutation = useMutation(createClientTag, {
    onSuccess: (newTag) => {
      message.success('Tag created successfully')
      // Automatically select the newly created tag
      if (!selectedTags.find((t) => t.id === newTag.id)) {
        const newSelectedTags = [...selectedTags, newTag]
        setSelectedTags(newSelectedTags)
        form.setFieldValue(
          field.key,
          newSelectedTags.map((t) => t.id)
        )
      }
      refetch() // Refresh the tags list
      setSearchValue('')
      setIsCreating(false)
    },
    onError: (error) => {
      message.error('Failed to create tag')
      console.error('Error creating tag:', error)
    },
  })

  // Delete tag mutation
  const deleteTagMutation = useMutation(deleteClientTag, {
    onSuccess: () => {
      message.success('Tag deleted successfully')
      refetch() // Refresh the tags list
    },
    onError: (error) => {
      message.error('Failed to delete tag')
      console.error('Error deleting tag:', error)
    },
  })

  const handleTagSelect = (tag) => {
    if (!selectedTags.find((t) => t.id === tag.id)) {
      const newSelectedTags = [...selectedTags, tag]
      setSelectedTags(newSelectedTags)
      form.setFieldValue(
        field.key,
        newSelectedTags.map((t) => t.id)
      )
    }
    setSearchValue('')
  }

  const handleTagRemove = (tagToRemove) => {
    const newSelectedTags = selectedTags.filter(
      (tag) => tag.id !== tagToRemove.id
    )
    setSelectedTags(newSelectedTags)
    form.setFieldValue(
      field.key,
      newSelectedTags.map((t) => t.id)
    )
  }

  const handleCreateTag = async () => {
    if (searchValue.trim()) {
      setIsCreating(true)
      const tagLabel = searchValue.trim()
      const tagKey = tagLabel
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
      createTagMutation.mutate({
        label: tagLabel,
        key: tagKey,
      })
    }
  }

  const handleDeleteTag = (tagToDelete) => {
    Modal.confirm({
      title: 'Delete Tag',
      content: `Are you sure you want to delete the tag "${tagToDelete.label}"? This will remove it from all customers.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        deleteTagMutation.mutate(tagToDelete.id)
      },
    })
  }

  const filteredTags =
    tagsData?.filter(
      (tag) =>
        tag.label.toLowerCase().includes(searchValue.toLowerCase()) &&
        !selectedTags.find((selected) => selected.id === tag.id)
    ) || []

  const showCreateOption =
    searchValue.trim() &&
    !tagsData?.find(
      (tag) => tag.label.toLowerCase() === searchValue.toLowerCase()
    )

  return (
    <Form.Item {...restProps}>
      <div>
        {/* Selected Tags Display */}
        <div style={{ marginBottom: 8, minHeight: 32 }}>
          {selectedTags.map((tag) => (
            <Tag
              key={tag.id}
              closable
              onClose={() => handleTagRemove(tag)}
              style={{ marginBottom: 4 }}
            >
              {tag.label}
            </Tag>
          ))}
        </div>

        {/* Search Input */}
        <AutoComplete
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search or create tags..."
          disabled={field.isInactive}
          style={{ width: '100%' }}
          options={[
            ...filteredTags.map((tag) => ({
              value: tag.label,
              label: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span
                    onClick={() => handleTagSelect(tag)}
                    style={{ flex: 1, cursor: 'pointer' }}
                  >
                    {tag.label}
                  </span>
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteTag(tag)
                    }}
                    style={{
                      color: '#ff4d4f',
                      cursor: 'pointer',
                      marginLeft: 8,
                      fontSize: '12px',
                      padding: '2px 4px',
                      borderRadius: '2px',
                      backgroundColor: '#fff2f0',
                      border: '1px solid #ffccc7',
                    }}
                    title="Delete tag"
                  >
                    ×
                  </span>
                </div>
              ),
            })),
            ...(showCreateOption
              ? [
                  {
                    value: `Create "${searchValue}"`,
                    label: (
                      <div
                        style={{
                          color: '#1890ff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span>+</span>
                        <span>Create "{searchValue}"</span>
                        {isCreating && <LoadingOutlined />}
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
          onSelect={(value) => {
            if (value.startsWith('Create "')) {
              handleCreateTag()
            } else {
              const selectedTag = tagsData?.find((tag) => tag.label === value)
              if (selectedTag) {
                handleTagSelect(selectedTag)
              }
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && showCreateOption) {
              e.preventDefault()
              handleCreateTag()
            }
          }}
        />
      </div>
    </Form.Item>
  )
}

export default TagSelector
